<script lang="ts">
  import { t } from '$i18n/index';
  import type { MessageKey } from '$i18n/index';
  import { UNTIL_CHOICES } from '$domain/events';
  import type { Resolution, UntilChoice } from '$domain/events';
  import { rotateOutcomes } from '$domain/comeback';
  import { app } from '$ui/app-state.svelte';
  import { resolveCatch } from '$app/catch-service';
  import type { SurfacedPlan } from '$app/catch-service';
  import { resolvedCount } from '$domain/reducers/catches';
  import { focusOnMount } from '$ui/focus';

  interface Props {
    catchId: string;
    /** Already recorded as surfaced before this screen was shown (spec §6.3). */
    plan: SurfacedPlan | null;
    ondone: () => void;
  }

  const { catchId, plan, ondone }: Props = $props();

  /**
   * The Comeback — tap 3, and optionally tap 4.
   *
   * **Equal dignity is a formal constraint here, not a tone note** (spec §7).
   * The three outcomes must not encode moral rank through hierarchy, size,
   * colour, iconography, order, animation, celebration or follow-up copy. In
   * practice that means:
   *
   * - one loop, one class, one element shape — there is no per-outcome styling
   *   to drift apart, because there is no per-outcome styling at all;
   * - the order comes from `rotateOutcomes`, so no outcome permanently owns the
   *   position under the thumb;
   * - none of them gets a hint, badge or sub-label the others lack;
   * - nothing is celebrated afterwards, including "Do what I planned".
   *
   * A test asserts the computed styling is identical. The test exists because
   * this is the constraint most certain to erode under ordinary UI work, and
   * the one no reviewer catches on the twentieth look at the screen.
   */

  let pendingUntil = $state(false);
  let committing = $state(false);
  let failed = $state(false);

  const order = $derived(rotateOutcomes(resolvedCount(app.derived.catches)));

  const outcomeLabels = $derived<Record<Resolution, MessageKey>>({
    redirect: plan ? 'resolve.redirect' : 'resolve.redirectNoPlan',
    surf: 'resolve.surf',
    deliberate: 'resolve.deliberate',
  });

  async function choose(resolution: Resolution) {
    if (resolution === 'deliberate') {
      // Tap 4 is offered, never required: "until when?" is optional, and
      // skipping it still completes the Comeback.
      pendingUntil = true;
      return;
    }
    await commit(resolution);
  }

  async function commit(resolution: Resolution, untilChoice?: UntilChoice) {
    // A second tap while the first is still writing would append a second
    // `catch.resolved` for the same Catch. The log is append-only, so that is
    // not a UI glitch that repaints away — it is two contradictory facts about
    // one moment, permanently.
    if (committing) return;
    committing = true;
    failed = false;

    try {
      const ctx = await app.context();
      await resolveCatch(ctx, catchId, resolution, {
        ...(plan ? { planId: plan.planId } : {}),
        ...(untilChoice !== undefined ? { untilChoice } : {}),
      });
      await app.refresh();
      ondone();
    } catch {
      // Storage can fail — private mode, no quota, an evicted database. Saying
      // so plainly and letting them tap again beats a screen that has quietly
      // stopped responding.
      committing = false;
      failed = true;
    }
  }
</script>

<main>
  {#if pendingUntil}
    <h1 tabindex="-1" use:focusOnMount>{t('until.question')}</h1>
    <ul class="options">
      {#each UNTIL_CHOICES as choice (choice)}
        <li>
          <button type="button" class="option" onclick={() => commit('deliberate', choice)}>
            {t(`until.${choice}` as MessageKey)}
          </button>
        </li>
      {/each}
    </ul>
    <button type="button" class="aside" onclick={() => commit('deliberate')}>
      {t('until.skip')}
    </button>
  {:else}
    <div class="plan" tabindex="-1" use:focusOnMount>
      {#if plan}
        <p class="action">{plan.action}</p>
        {#if plan.anchor}
          <p class="anchor">{t('resolve.anchor', { direction: plan.anchor })}</p>
        {/if}
        <p class="cue">{t('resolve.sayItCue')}</p>
      {/if}
    </div>

    <ul class="options">
      {#each order as outcome (outcome)}
        <li>
          <button type="button" class="option" onclick={() => choose(outcome)}>
            {t(outcomeLabels[outcome])}
          </button>
        </li>
      {/each}
    </ul>

    <!-- Not a fourth outcome. Leaving here is a completed Catch — a rep of
         skill one — so it is neither ranked among the three nor styled as a
         lesser version of them. -->
    <button type="button" class="aside" onclick={ondone}>{t('resolve.leave')}</button>
  {/if}

  <p class="failed" role="alert">{failed ? t('error.notSaved') : ''}</p>
</main>

<style>
  /* Bottom-weighted for thumb reach, same reason as the Catch button. */
  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: var(--space-5);
    padding: var(--space-6) var(--space-5) var(--space-7);
  }

  h1 {
    font-size: var(--step-1);
    font-weight: 500;
    color: var(--c-ink-muted);
    text-align: center;
  }

  .plan {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .action {
    font-size: var(--step-1);
    color: var(--c-ink);
  }

  .anchor {
    font-size: var(--step-0);
    font-style: italic;
    color: var(--c-ink-muted);
  }

  .cue {
    font-size: var(--step--1);
    color: var(--c-ink-faint);
    font-style: italic;
  }

  .options {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  /*
   * One rule for all three outcomes.
   *
   * Do not add `.option--redirect`, a primary variant, an accent border, an
   * icon on one of them, or an order that stops rotating. The equal-dignity
   * test will fail, and it is right to.
   */
  .option {
    width: 100%;
    padding: var(--space-4);
    border-radius: var(--radius);
    border: 1px solid var(--c-line);
    background: var(--c-surface);
    color: var(--c-ink);
    font-size: var(--step-1);
    font-weight: 400;
    text-align: left;
  }

  .option:active {
    background: var(--c-surface-raised);
  }

  .aside {
    align-self: center;
    color: var(--c-ink-faint);
    font-size: var(--step--1);
    padding: var(--space-2) var(--space-4);
  }

  /* Reserves its line so nothing shifts under a thumb when it appears. */
  .failed {
    min-height: 1.5em;
    text-align: center;
    color: var(--c-ink-muted);
    font-size: var(--step--1);
  }
</style>
